import { Query, useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';

import { InventoryService } from 'api/services';
import { PageInfo } from 'components/Navigation';

import { DataAccessFilters } from './utils';

/** The identity the tab is open for. */
export interface DataAccessIdentity {
  uuid: string;
  accountId: string;
}

const KEY = 'snowflake-identity-data-access';

// Each request is keyed by everything it sends, so a response for another
// identity, filter or page is never shown in place of the current one. A
// failure shows in place, with a retry, rather than retrying on its own.
const options = {
  retry: false,
  // Opening a closed row again within a minute shows it without a request.
  staleTime: 60_000,
};

/**
 * Keeps the page on screen while the next page or filter loads, but never
 * another identity's: the drawer reuses the tab for the next user it opens.
 */
const keepForIdentity = (identity: DataAccessIdentity) => <T>(
  previous: T | undefined,
  previousQuery?: Query<T, any, T, any>,
) => (isEqual(previousQuery?.queryKey?.[2], identity) ? previous : undefined);

const filterParams = (filters: DataAccessFilters) => ({
  highPrivilegeOnly: filters.highPrivilegeOnly,
  sensitiveOnly: filters.sensitiveOnly,
});

const pageParams = (page: PageInfo) => ({
  page: page.page_number,
  size: page.page_size,
});

/** The header counts, and whether the account's rows are computed yet. */
export const useDataAccessSummary = (identity: DataAccessIdentity) =>
  useQuery({
    ...options,
    queryKey: [KEY, 'summary', identity],
    queryFn: ({ signal }) =>
      InventoryService.Insight.getIdentityDataAccessSummary(identity, {
        signal,
      }).then(r => r.data),
    enabled: !!identity.uuid,
  });

/** One page of databases. */
export const useDataAccessSources = (
  identity: DataAccessIdentity,
  filters: DataAccessFilters,
  page: PageInfo,
) =>
  useQuery({
    ...options,
    queryKey: [KEY, 'sources', identity, filters, page],
    queryFn: ({ signal }) =>
      InventoryService.Insight.getIdentityDataAccessSources(
        { ...identity, ...filterParams(filters), ...pageParams(page) },
        { signal },
      ).then(r => r.data),
    placeholderData: keepForIdentity(identity),
    enabled: !!identity.uuid,
  });

/** One page of the schemas in an open database, read live. */
export const useDataAccessSchemas = (
  identity: DataAccessIdentity,
  databaseUuid: string,
  filters: DataAccessFilters,
  page: PageInfo,
) =>
  useQuery({
    ...options,
    queryKey: [KEY, 'schemas', identity, databaseUuid, filters, page],
    queryFn: ({ signal }) =>
      InventoryService.Insight.getIdentityDataAccessSchemas(
        {
          ...identity,
          databaseUuid,
          ...filterParams(filters),
          ...pageParams(page),
        },
        { signal },
      ).then(r => r.data),
    placeholderData: keepForIdentity(identity),
  });

/** One page of the tables in an open schema, read live. */
export const useDataAccessTables = (
  identity: DataAccessIdentity,
  schemaUuid: string,
  filters: DataAccessFilters,
  page: PageInfo,
) =>
  useQuery({
    ...options,
    queryKey: [KEY, 'tables', identity, schemaUuid, filters, page],
    queryFn: ({ signal }) =>
      InventoryService.Insight.getIdentityDataAccessTables(
        {
          ...identity,
          schemaUuid,
          ...filterParams(filters),
          ...pageParams(page),
        },
        { signal },
      ).then(r => r.data),
    placeholderData: keepForIdentity(identity),
  });

/** An open table's privileges, owner, tags and one page of granting roles. */
export const useDataAccessTableDetails = (
  identity: DataAccessIdentity,
  tableUuid: string,
  page: PageInfo,
) =>
  useQuery({
    ...options,
    queryKey: [KEY, 'table-details', identity, tableUuid, page],
    queryFn: ({ signal }) =>
      InventoryService.Insight.getIdentityDataAccessTableDetails(
        { ...identity, tableUuid, ...pageParams(page) },
        { signal },
      ).then(r => r.data),
    placeholderData: keepForIdentity(identity),
  });
